import { RoleType } from "@mc-intranet/shared";
import CssBaseline from "@mui/material/CssBaseline";
import GlobalStyles from "@mui/material/GlobalStyles";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { LicenseInfo } from "@mui/x-license-pro";
import { useAtom, useAtomValue } from "jotai";
import React, { useEffect, useMemo } from "react";
import { Route, Routes } from "react-router-dom";

import { authStorageAtom, modeAtom } from "../atoms";
import PrivateRoute from "../components/privateroute";
import Layout from "../components/ui/Layout";
import AppMobilePage from "../pages/app-mobile";
import Approvazione from "../pages/approvazione";
import ForgotPasswordPage from "../pages/forgot-password";
import Home from "../pages/home";
import HoursOff from "../pages/hoursOff";
import LoginPage from "../pages/login";
import NotFound from "../pages/NotFound";
import Notifications from "../pages/notifications";
import Presenze from "../pages/presenze";
import PrivacyPage from "../pages/privacy";
import ResetPasswordPage from "../pages/reset-password";
import StatsPage from "../pages/stats";
import Timbrature from "../pages/timbrature";
import Today from "../pages/today";
import Vacations from "../pages/vacations";
import { ColorModeContext, getDesignTokens, MyThemeOptions } from "../theme";

LicenseInfo.setLicenseKey(
  "cf8b7403d9a75fee7a761b24e561fc5aT1JERVI6MzU2NTcsRVhQSVJZPTE2NzM0NTA5NjIwMDAsS0VZVkVSU0lPTj0x"
);

function App() {
  const prefersDarkMode =
    typeof window !== "undefined"
      ? window.matchMedia &&
        window.matchMedia("(prefers-color-scheme: dark)").matches
      : false;
  // console.log("prefersDarkMode :>> ", prefersDarkMode);

  const [mode, setMode] = useAtom(modeAtom);
  // console.log("mode :>> ", mode);

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
      }
    }),
    [setMode]
  );

  const theme = React.useMemo(
    () =>
      createTheme(
        getDesignTokens(
          !mode ? (prefersDarkMode ? "dark" : "light") : mode
        ) as MyThemeOptions
      ),
    [mode, prefersDarkMode]
  );
  const authStorage = useAtomValue(authStorageAtom);
  const { user, role } = authStorage;

  useEffect(() => {
    if (!mode) {
      setMode(prefersDarkMode ? "dark" : "light");
    }
  }, [mode, prefersDarkMode, setMode]);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
        <CssBaseline />
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <Routes>
            <Route path="/" element={<Layout />}>
              <Route path="/" element={<LoginPage />} />
              <Route
                path="/home"
                element={
                  <PrivateRoute user={user}>
                    <Home />
                  </PrivateRoute>
                }
              />
              <Route
                path="/presenze"
                element={
                  <PrivateRoute user={user}>
                    <Presenze />
                  </PrivateRoute>
                }
              />
              <Route
                path="/oggi"
                element={
                  <PrivateRoute user={user}>
                    <Today />
                  </PrivateRoute>
                }
              />
              <Route
                path="/ferie"
                element={
                  <PrivateRoute user={user}>
                    <Vacations />
                  </PrivateRoute>
                }
              />
              <Route
                path="/permessi"
                element={
                  <PrivateRoute user={user}>
                    <HoursOff />
                  </PrivateRoute>
                }
              />
              <Route
                path="/stats"
                element={
                  <PrivateRoute user={user}>
                    <StatsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/notifiche"
                element={
                  <PrivateRoute
                    user={user}
                    restricted={!role || role !== RoleType.admin}
                  >
                    <Notifications />
                  </PrivateRoute>
                }
              />
              <Route
                path="/timbrature"
                element={
                  <PrivateRoute user={user} restricted={!role || role !== RoleType.admin}>
                    <Timbrature />
                  </PrivateRoute>
                }
              />
              <Route
                path="/approvazione"
                element={
                  <PrivateRoute user={user} restricted={!role || role !== RoleType.admin}>
                    <Approvazione />
                  </PrivateRoute>
                }
              />
              <Route path="/app-mobile" element={<AppMobilePage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route
                path="/reset-password/:code"
                element={<ResetPasswordPage />}
              />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
          <GlobalStyles
            styles={(theme) => ({
              a: { color: theme.palette.primary.main },
              ":root": {
                "--fc-small-font-size": ".95em",
                "--fc-page-bg-color": theme.palette.background.default,
                "--fc-neutral-bg-color": "rgba(208, 208, 208, 0.3)",
                "--fc-neutral-text-color": "#808080",
                "--fc-border-color": theme.palette.background.paper,

                "--fc-button-text-color": theme.palette.text.primary,
                "--fc-button-bg-color": theme.palette.background.default,
                "--fc-button-border-color": theme.palette.text.primary,
                "--fc-button-hover-bg-color": theme.palette.background.paper,
                "--fc-button-hover-border-color": theme.palette.primary.main,
                "--fc-button-active-bg-color": theme.palette.background.paper,
                "--fc-button-active-border-color": theme.palette.primary.main,

                "--fc-event-bg-color": "#3788d8",
                "--fc-event-border-color": "#3788d8",
                "--fc-event-text-color": "#fff",
                "--fc-event-selected-overlay-color": "rgba(0, 0, 0, 0.25)",

                "--fc-more-link-bg-color": "#d0d0d0",
                "--fc-more-link-text-color": "inherit",

                "--fc-event-resizer-thickness": "8px",
                "--fc-event-resizer-dot-total-width": "8px",
                "--fc-event-resizer-dot-border-width": "1px",

                "--fc-non-business-color": "rgba(215, 215, 215, 0.3)",
                "--fc-bg-event-color": "rgb(143, 223, 130, 0.3)",
                "--fc-bg-event-opacity": "0.3",
                "--fc-highlight-color": "rgba(188, 232, 241, 0.3)",
                "--fc-today-bg-color": "rgba(231, 52, 76, 0.2)",
                "--fc-now-indicator-color": theme.palette.primary.main
              },
              ".fc-theme-standard": {
                "@media (max-width: 700px)": {
                  minHeight: "70vh"
                },
                ".fc-header-toolbar": {
                  "@media (max-width: 700px)": {
                    flexDirection: "column"
                  }
                },
                ".fc-scrollgrid": {
                  backgroundColor: theme.palette.background.default,
                  border: `0px solid var(--fc-border-color, ${theme.palette.background.paper})`
                },
                table: {
                  fontSize: "14px",
                  th: {
                    backgroundColor: theme.palette.background.paper,
                    border: "none",
                    borderLeft: `8px solid ${theme.palette.background.paper}`,
                    textAlign: "right",
                    "@media (max-width: 700px)": {
                      borderLeft: `2px solid ${theme.palette.background.paper}`
                    },
                    a: { color: theme.palette.text.primary }
                  },
                  td: {
                    border: "none",
                    borderLeft: `8px solid ${theme.palette.background.paper}`,
                    height: "100px",
                    "@media (max-width: 700px)": {
                      borderLeft: `2px solid ${theme.palette.background.paper}`,
                      height: "auto"
                    },
                    ".fc-daygrid-day-frame": {
                      borderTop: `2px solid var(--fc-border-color, ${theme.palette.background.paper})`
                    },
                    ".fc-daygrid-day.fc-day-today": {
                      ".fc-daygrid-day-frame": {
                        borderTopColor: theme.palette.primary.main,

                        ".fc-daygrid-day-number": {
                          color: theme.palette.primary.main
                        }
                      }
                    },
                    ".fc-day.fc-day-sun, .fc-day.fc-day-sat": {
                      backgroundColor: theme.palette.bgs.weekend
                    },
                    ".fc-daygrid-day-events": {
                      pointerEvents: "none"
                    },
                    ".fc-bg-event": {
                      opacity: 1,
                      overflow: "hidden",
                      ".fc-event-title": {
                        color: theme.palette.text.primary,
                        opacity: 0.55,
                        overflow: "hidden",
                        "@media (max-width: 700px)": {
                          fontSize: "9px",
                          marginRight: "12px",
                          wordBreak: "break-all",
                          lineHeight: "9px"
                        }
                      }
                    }
                  }
                }
              }
            })}
          />
        </LocalizationProvider>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
